import { extractYoutubeVideoId, youtubeEmbedUrl } from "@coinfallsmp/shared";
import { useState } from "react";

/**
 * Shows whether an uploaded/imported video's file has actually landed and
 * is playable yet, plus a real inline preview once it has — there was
 * previously no way to check a video plays before publishing it, and no
 * feedback distinguishing "still buffering" from "something's wrong" once
 * playback did start. A YouTube-embedded video is always "ready" (there's
 * no file to wait on) and previews as the same embed visitors will see.
 */
export default function VideoPreview({
  videoId,
  ready,
  sourceType,
  sourceUrl,
}: {
  videoId: string;
  ready: boolean;
  sourceType: "upload" | "youtube";
  sourceUrl: string | null;
}) {
  const [buffering, setBuffering] = useState(true);
  const [playbackError, setPlaybackError] = useState(false);

  const youtubeId = sourceType === "youtube" && sourceUrl ? extractYoutubeVideoId(sourceUrl) : null;

  if (youtubeId) {
    return (
      <div className="preview-box preview-ready">
        <div className="preview-ready-label">
          <span className="ready-dot" />
          Embedded from YouTube
        </div>
        <div className="preview-player">
          <iframe
            className="preview-embed"
            src={youtubeEmbedUrl(youtubeId, window.location.origin)}
            title="YouTube video preview"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="preview-box preview-not-ready">
        <span className="spinner" />
        Not ready yet — waiting for the file to finish uploading or
        importing. This updates automatically.
      </div>
    );
  }

  return (
    <div className="preview-box preview-ready">
      <div className="preview-ready-label">
        <span className="ready-dot" />
        File ready
      </div>
      <div className="preview-player">
        {playbackError ? (
          <div className="preview-buffering-overlay preview-error-overlay">
            Can't play this file. Its actual format isn't one browsers
            support (check the codec — H.264/AAC in .mp4 or VP8/VP9 in
            .webm work; other containers like MPEG-2 don't), regardless of
            the file extension. Re-upload a browser-native export to fix
            this.
          </div>
        ) : buffering ? (
          <div className="preview-buffering-overlay">
            <span className="spinner" />
            Buffering…
          </div>
        ) : null}
        <video
          controls
          preload="metadata"
          src={`/api/admin/videos/${videoId}/stream`}
          onWaiting={() => setBuffering(true)}
          onCanPlay={() => setBuffering(false)}
          onPlaying={() => setBuffering(false)}
          onError={() => setPlaybackError(true)}
        />
      </div>
    </div>
  );
}
